import React from 'react';
import { Persona } from '../../types/persona';

interface PersonaCardProps {
  persona: Persona;
  onClick: () => void;
}

// Color coding for brand relationships
const getBrandRelationshipColor = (usageStatus: string): string => {
  const status = usageStatus.toLowerCase();
  if (status.includes('superfan') || status.includes('super fan')) return 'bg-green-100 text-green-800 border-green-200';
  if (status.includes('user') || status.includes('regular')) return 'bg-blue-100 text-blue-800 border-blue-200';
  if (status.includes('casual') || status.includes('occasional')) return 'bg-gray-100 text-gray-800 border-gray-200';
  if (status.includes('skeptic') || status.includes('aware')) return 'bg-orange-100 text-orange-800 border-orange-200';
  if (status.includes('non-user') || status.includes('nonuser') || status.includes('never')) return 'bg-red-100 text-red-800 border-red-200';
  return 'bg-gray-100 text-gray-800 border-gray-200';
};

// Generate initials from name
const getInitials = (name: string): string => {
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Get avatar color based on name
const getAvatarColor = (name: string): string => {
  const colors = [
    'bg-primary-500',
    'bg-secondary-500',
    'bg-pink-500',
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-red-500',
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

export const PersonaCard: React.FC<PersonaCardProps> = ({ persona, onClick }) => {
  const brandColor = getBrandRelationshipColor(persona.brand_relationship.usage_status);
  const avatarColor = getAvatarColor(persona.name);
  const initials = getInitials(persona.name);

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer border border-gray-200 overflow-hidden group"
    >
      {/* Header with Avatar and Name */}
      <div className="p-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="flex items-center space-x-3">
          {/* Avatar - show image if available, otherwise show initials */}
          {persona.avatarUrl ? (
            <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-primary-200">
              <img
                src={persona.avatarUrl}
                alt={persona.name}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className={`w-12 h-12 rounded-full ${avatarColor} flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
              {initials}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate group-hover:text-primary-500 transition-colors">
              {persona.name}
            </h3>
            <p className="text-sm text-gray-500">
              {persona.demographics.age} • {persona.demographics.gender}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Location and Occupation */}
        <div className="space-y-1">
          <div className="flex items-center text-sm text-gray-600">
            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="truncate">{persona.demographics.location}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="truncate">{persona.demographics.occupation}</span>
          </div>
        </div>

        {/* Brand Relationship Badge */}
        <div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${brandColor}`}>
            {persona.brand_relationship.usage_status}
          </span>
        </div>

        {/* Knowledge Indicators */}
        <div className="pt-2 border-t border-gray-100 space-y-2">
          <div className="flex justify-between items-center text-xs">
            <span className="text-gray-500">Specific Knowledge</span>
            <span className="font-medium text-gray-700">{persona.cognitive.knowledge_specific}/100</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-primary-500 h-1.5 rounded-full transition-all"
              style={{ width: `${persona.cognitive.knowledge_specific}%` }}
            />
          </div>
        </div>

        {/* Quick Stats */}
        <div className="pt-2 border-t border-gray-100 flex justify-between text-xs text-gray-500">
          <span>IQ: {persona.cognitive.iq}</span>
          <span>Critical Thinking: {persona.cognitive.critical_thinking}</span>
        </div>
      </div>
    </div>
  );
};
